import React, { useState, useEffect, useRef, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import DOMPurify from 'dompurify';
import { PrismLight as SyntaxHighlighter } from 'react-syntax-highlighter';
import { dracula } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { BLOGS } from '../data/mockContent';
import {
  AiQuota,
  ApiError,
  adminDeletePost,
  askAboutPost,
  getPost,
  isApiConfigured,
  listCategories,
} from '../lib/api';
import { useProfile } from '../lib/useProfile';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { useAuthorControls } from '../lib/useAuthorControls';
import { isLegacySlug } from '../lib/useBlogIndex';
import { Category, Post } from '../types/content';
import './BlogDetail.css';

// Import languages for syntax highlighting
import javascript from 'react-syntax-highlighter/dist/esm/languages/prism/javascript';
import python from 'react-syntax-highlighter/dist/esm/languages/prism/python';
import bash from 'react-syntax-highlighter/dist/esm/languages/prism/bash';
import json from 'react-syntax-highlighter/dist/esm/languages/prism/json';
import go from 'react-syntax-highlighter/dist/esm/languages/prism/go';
import lua from 'react-syntax-highlighter/dist/esm/languages/prism/lua';

// Register languages
SyntaxHighlighter.registerLanguage('javascript', javascript);
SyntaxHighlighter.registerLanguage('python', python);
SyntaxHighlighter.registerLanguage('bash', bash);
SyntaxHighlighter.registerLanguage('json', json);
SyntaxHighlighter.registerLanguage('go', go);
SyntaxHighlighter.registerLanguage('lua', lua);

// --- Types ---
type Message = {
  id: number;
  text: string;
  sender: 'user' | 'ai';
};

/** What the article header needs, whether it came from the API or a .md file. */
interface ArticleMeta {
  title: string;
  excerpt: string;
  authorName: string;
  publishedAt: string | null;
  readingMinutes: number;
  tags: string[];
  category: string | null;
  coverImage: string | null;
}

// --- Markdown Components Configuration (legacy .md articles) ---
const MarkdownComponents = {
  code({ node, inline, className, children, ...props }: any) {
    const match = /language-(\w+)/.exec(className || '');
    return !inline && match ? (
      <div className="code-block-wrapper">
          <div className="code-header">
              <span className="window-dot red"></span>
              <span className="window-dot yellow"></span>
              <span className="window-dot green"></span>
              <span className="language-label">{match[1]}</span>
          </div>
          <SyntaxHighlighter
          style={dracula}
          language={match[1]}
          PreTag="div"
          customStyle={{ margin: 0, borderRadius: '0 0 8px 8px' }}
          {...props}
          >
          {String(children).replace(/\n$/, '')}
          </SyntaxHighlighter>
      </div>
    ) : (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
  img: ({node, ...props}: any) => {
      let { src, ...rest } = props;
      if (src && !src.startsWith('http') && !src.startsWith('data:')) {
          const publicUrl = process.env.PUBLIC_URL || '';
          const cleanSrc = src.startsWith('/') ? src : `/${src}`;
          src = `${publicUrl}${cleanSrc}`;
      }
      return <img className="article-image" src={src} {...rest} alt={props.alt || ''} />;
  },
  table: ({node, ...props}: any) => (
      <div className="table-container">
          <table className="article-table" {...props} />
      </div>
  )
};

// --- Helper SVG Icons ---
const SendIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.01 21L23 12L2.01 3L2 10L17 12L2 14L2.01 21Z" fill="currentColor"/>
  </svg>
);

const ChatIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H6L4 18V4H20V16Z" fill="currentColor"/>
    </svg>
);

const CloseIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19 6.41L17.59 5L12 10.59L6.41 5L5 6.41L10.59 12L5 17.59L6.41 19L12 13.41L17.59 19L19 17.59L13.41 12L19 6.41Z" fill="currentColor"/>
    </svg>
);

const LoadingIndicator = () => (
    <div className="chat-bubble ai loading-indicator">
        <div className="loading-dots">
            <span></span>
            <span></span>
            <span></span>
        </div>
    </div>
);

const formatDate = (iso: string | null) =>
  iso
    ? new Date(iso).toLocaleDateString(undefined, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '';

const BlogDetail: React.FC = () => {
  // Kept as `id` so existing /blog/:id links stay valid — it is the slug.
  const { id: slug } = useParams<{ id: string }>();

  const navigate = useNavigate();
  const [postId, setPostId] = useState<string | null>(null);
  const [meta, setMeta] = useState<ArticleMeta | null>(null);
  const [articleHtml, setArticleHtml] = useState<string | null>(null);
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [isContentLoading, setIsContentLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const chatWindowRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    window.scrollTo(0, 0);
    setIsChatOpen(false);
    setIsContentLoading(true);
    setNotFound(false);
    setArticleHtml(null);
    setMarkdownContent('');
    setPostId(null);

    const applyMeta = (next: ArticleMeta) => {
      if (cancelled) return;
      setMeta(next);
      setMessages([
        {
          id: 1,
          sender: 'ai',
          text: `Hello! I'm your **QED AI Tutor**.

**Blog Summary:**
${next.excerpt}

*Note: Interactive deep-reasoning for this architecture is currently **under implementation**. I can currently answer general system design questions.*`,
        },
      ]);
    };

    const loadLegacy = () => {
      const blog = BLOGS.find((b) => b.id === slug);
      if (!blog) {
        if (!cancelled) {
          setNotFound(true);
          setIsContentLoading(false);
        }
        return;
      }

      applyMeta({
        title: blog.title,
        excerpt: blog.excerpt,
        authorName: blog.author,
        publishedAt: blog.date,
        readingMinutes: 8,
        tags: blog.tags,
        category: 'system-design',
        coverImage: null,
      });

      fetch(`${process.env.PUBLIC_URL}/content/${blog.id}.md`)
        .then((res) => {
          if (!res.ok) throw new Error('Failed to load content');
          return res.text();
        })
        .then((text) => {
          if (cancelled) return;
          setMarkdownContent(text);
          setTimeout(() => setIsContentLoading(false), 50);
        })
        .catch(() => {
          if (cancelled) return;
          setMarkdownContent('Failed to load article content. Please try again later.');
          setIsContentLoading(false);
        });
    };

    // The three original markdown articles never round-trip through the API.
    if (!slug) {
      setNotFound(true);
      setIsContentLoading(false);
      return;
    }

    if (isLegacySlug(slug) || !isApiConfigured) {
      loadLegacy();
      return () => {
        cancelled = true;
      };
    }

    (async () => {
      try {
        const post: Post = await getPost(slug);
        if (cancelled) return;

        setPostId(post.id);
        applyMeta({
          title: post.title,
          excerpt: post.excerpt,
          authorName: post.authorName,
          publishedAt: post.publishedAt,
          readingMinutes: post.readingMinutes,
          tags: post.tags,
          category: post.category,
          coverImage: post.coverImage,
        });

        // Sanitized on the server too; this is the second line of defence and
        // covers anything that predates the current whitelist.
        //
        // `iframe` is opted in explicitly because DOMPurify strips it by
        // default. Safe here only because the server pinned the src to video
        // hosts before storing, and the sandbox attribute below denies the
        // frame everything except playback.
        setArticleHtml(
          DOMPurify.sanitize(post.contentHtml, {
            ADD_TAGS: ['iframe'],
            ADD_ATTR: [
              'allow',
              'allowfullscreen',
              'frameborder',
              'target',
              'data-align',
              'data-caption',
              'data-variant',
              'data-callout',
              'data-type',
              'data-checked',
              'data-youtube-video',
            ],
          })
        );
        setTimeout(() => {
          if (!cancelled) setIsContentLoading(false);
        }, 50);
      } catch {
        if (!cancelled) loadLegacy();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [slug]);

  useEffect(() => {
    if (!isApiConfigured) return;
    let cancelled = false;
    listCategories()
      .then((list) => {
        if (!cancelled) setCategories(list);
      })
      .catch(() => {
        /* The section pill is decoration; failing to load it is fine. */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const categoryName = useMemo(() => {
    if (!meta?.category) return null;
    return categories.find((c) => c.slug === meta.category)?.name || null;
  }, [categories, meta]);

  // Renders nothing for readers; only a signed-in admin triggers the lookup.
  const author = useAuthorControls(postId);

  const { profile } = useProfile();

  // The share sheet reads document.title, so this is what makes sharing from
  // inside an article carry the article's name rather than the home page's.
  useDocumentTitle(meta?.title);
  const [quota, setQuota] = useState<AiQuota | null>(null);

  const handleDelete = async () => {
    if (!postId || !meta) return;
    if (!window.confirm(`Delete "${meta.title}"? Its images are removed too.`)) return;
    try {
      await adminDeletePost(postId);
      navigate('/', { replace: true });
    } catch (e) {
      window.alert(e instanceof Error ? e.message : 'Could not delete the post.');
    }
  };

  useEffect(() => {
    if (chatWindowRef.current) {
      chatWindowRef.current.scrollTop = chatWindowRef.current.scrollHeight;
    }
  }, [messages, isLoading, isChatOpen]);

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    const question = input.trim();
    if (!question || isLoading) return;

    // Reporting requires an account, and so does this: the daily allowance is
    // per person, and an anonymous cap could only key on IP.
    if (!profile) {
      setMessages((prev) => [
        ...prev,
        { id: Date.now(), text: question, sender: 'user' },
        {
          id: Date.now() + 1,
          text: 'Please sign in to ask about this article. Everyone signed in gets 20 questions a day.',
          sender: 'ai',
        },
      ]);
      setInput('');
      return;
    }

    if (!postId) return;

    setMessages((prev) => [...prev, { id: Date.now(), text: question, sender: 'user' }]);
    setInput('');
    setIsLoading(true);

    try {
      const result = await askAboutPost(postId, question);
      setQuota(result.quota);
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          // The cap is generous now, so hitting it is unusual and worth
          // saying plainly rather than appending a cryptic marker.
          text: result.truncated
            ? `${result.answer}

_That answer hit the length limit. Ask about one part of it for more detail._`
            : result.answer,
          sender: 'ai',
        },
      ]);
    } catch (error) {
      // The distinctions worth surfacing: out of questions, assistant down, or
      // something else. Each has a different thing for the reader to do.
      const status = error instanceof ApiError ? error.status : 0;
      const text =
        status === 429
          ? (error as ApiError).message
          : status === 503
            ? 'The assistant is unavailable right now. The article itself is unaffected.'
            : error instanceof Error
              ? error.message
              : 'Something went wrong. Please try again.';
      setMessages((prev) => [...prev, { id: Date.now() + 1, text, sender: 'ai' }]);
    } finally {
      setIsLoading(false);
    }
  };

  if (notFound || !meta) {
    if (isContentLoading && !notFound) {
      return <div className="not-found-container">Loading article…</div>;
    }
    return (
        <div className="not-found-container">
            <h2>Blog Post Not Found</h2>
            <Link to="/" className="back-link">Return to Home</Link>
        </div>
    );
  }

  return (
    <div className={`blog-detail-container ${isContentLoading ? 'loading' : 'loaded'}`}>
      <main className="blog-panel">
        {author.canEdit && (
          <div className="author-bar">
            <span className="author-bar-label">
              Your post
              {author.status && (
                <span className={`status-pill ${author.status}`}>{author.status}</span>
              )}
            </span>

            <div className="author-bar-actions">
              <Link to={`/admin/edit/${postId}`} className="author-bar-btn">
                Edit
              </Link>
              <button
                type="button"
                className="author-bar-btn"
                disabled={author.busy}
                onClick={() =>
                  author.setStatus(author.status === 'published' ? 'draft' : 'published')
                }
              >
                {author.busy
                  ? 'Saving…'
                  : author.status === 'published'
                    ? 'Unpublish'
                    : 'Publish'}
              </button>
              {author.canDelete && (
                <button type="button" className="author-bar-btn danger" onClick={handleDelete}>
                  Delete
                </button>
              )}
            </div>

            {author.error && <span className="author-bar-error">{author.error}</span>}
          </div>
        )}

        <div className="blog-header">
             <Link to="/" className="back-link-small">← Back</Link>
             {categoryName && (
                <Link to={`/category/${meta.category}`} className="article-category">
                    {categoryName}
                </Link>
             )}
             <h1>{meta.title}</h1>
             <div className="blog-meta">
                 <span>{meta.authorName}</span> • <span>{formatDate(meta.publishedAt)}</span>
                 {' • '}<span>{meta.readingMinutes} min read</span>
             </div>
             {meta.tags.length > 0 && (
                <div className="article-tags">
                    {meta.tags.map((tag) => (
                        <span key={tag} className="article-tag">{tag}</span>
                    ))}
                </div>
             )}
        </div>

        {meta.coverImage && (
            <img className="article-cover" src={meta.coverImage} alt="" />
        )}

        <div className="markdown-content">
            {articleHtml !== null ? (
                // Content is sanitized above, and again on the server before it
                // is ever stored.
                <div dangerouslySetInnerHTML={{ __html: articleHtml }} />
            ) : (
                <ReactMarkdown
                    remarkPlugins={[remarkGfm]}
                    components={MarkdownComponents}
                >
                    {markdownContent}
                </ReactMarkdown>
            )}
        </div>

        <div className="ai-integrated-callout">
            <div className="ai-callout-icon">
                <ChatIcon />
            </div>
            <div className="ai-callout-content">
                <h3>Deepen your understanding</h3>
                <p>Have questions about {meta.title}? Ask our AI System Design Tutor for real-time explanations.</p>
                <button className="ai-callout-btn" onClick={() => setIsChatOpen(true)}>
                    Talk via AI Assistant
                </button>
            </div>
        </div>
      </main>

      <aside className={`chat-panel ${isChatOpen ? 'open' : ''}`}>
        <div className="chat-header">
          <h2 className="chat-title">
            Ask about this article
            {quota && !quota.unlimited && (
              <span className="chat-quota"> · {quota.remaining} of {quota.limit} left today</span>
            )}
          </h2>
          <button className="chat-close-btn" onClick={() => setIsChatOpen(false)} aria-label="Close chat">
              <CloseIcon />
          </button>
        </div>
        <div className="chat-window" ref={chatWindowRef}>
          {messages.map((msg) => (
            <div key={msg.id} className={`chat-bubble ${msg.sender}`}>
              {msg.sender === 'ai' ? (
                  <ReactMarkdown>{msg.text}</ReactMarkdown>
              ) : (
                  msg.text
              )}
            </div>
          ))}
          {isLoading && <LoadingIndicator />}
        </div>
        <div className="chat-input-area">
          <form className="chat-input-form" onSubmit={handleSendMessage}>
            <input
              type="text"
              className="chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask a question..."
              aria-label="Chat input"
            />
            <button type="submit" className="send-button" disabled={!input.trim() || isLoading} aria-label="Send message">
              <SendIcon />
            </button>
          </form>
        </div>
      </aside>
    </div>
  );
};

export default BlogDetail;
